import React, { useEffect, useState } from "react";
import {
  getAffiliationsList,
  deleteAffiliation,
} from "../../../services/affiliationsService";
import { paginateData, calculateTotalPages } from "../../../assets/js/script";
import TableHeader from "../../Common/TableComponent/TableHeader";
import TableToolbar from "../../Common/TableComponent/TableToolbar";
import TablesRow from "../../Common/TableComponent/TablesRow";
import { renderStatusBadge } from "../../Common/TableComponent/tableBadges";
import { Pagination } from "../../Common/TableComponent/Pagination";
import { AddAffiliation } from "./AddAffiliation";
import { Loading } from "../../Common/OtherElements/Loading";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { confirmDelete } from "../../Common/OtherElements/confirmDeleteClone";
import { TableDataStatusError } from "../../Common/OtherElements/TableDataStatusError";
import { handleErrors } from "../../../utils/errorHandler";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import allImages from "../../../assets/images-import";
import ComponentHeader from "../../Common/OtherElements/ComponentHeader";

const getLogoUrl = (item) =>
  item.affiliationLogoUrl ||
  item.AffiliationLogoUrl ||
  item.logoUrl ||
  "";

const getWebsiteUrl = (item) =>
  item.affiliationWebsiteUrl || item.AffiliationWebsiteUrl || "";

const getAffiliationId = (item) => item.id ?? item.Id;

const getAffiliationName = (item) =>
  item.affiliationName || item.AffiliationName || "";

const getDisplayOrder = (item) =>
  item.displayOrder ?? item.DisplayOrder ?? "-";

const normalizeWebsiteHref = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
};

export const ManageAffiliationsContent = () => {
  const [pageAccessDetails, setPageAccessDetails] = useState({});
  const pageLevelAccessUrl = "affiliations";
  const navigate = useNavigate();
  const { pageAccessData } = usePageLevelAccess(pageLevelAccessUrl);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [affiliationsList, setAffiliationsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAffiliation, setSelectedAffiliation] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.addAccess && !pageAccessData.viewAccess) {
        navigate("/404-error-page");
      } else {
        setPageAccessDetails(pageAccessData);
      }
    }
  }, [pageAccessData, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getAffiliationsList();
      const list = response.data?.result || response.data || [];
      setAffiliationsList(Array.isArray(list) ? list : []);
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredData = affiliationsList.filter((item) => {
    const name = getAffiliationName(item).toLowerCase();
    const website = getWebsiteUrl(item).toLowerCase();
    const query = searchQuery.toLowerCase();
    return name.includes(query) || website.includes(query);
  });

  const currentData = paginateData(filteredData, currentPage, entriesPerPage);
  const totalPages = calculateTotalPages(filteredData.length, entriesPerPage);

  const handleEntriesChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleDelete = async (affiliationId) => {
    const confirmed = await confirmDelete("Affiliation");
    if (confirmed) {
      try {
        await deleteAffiliation(affiliationId);
        setAffiliationsList((prev) =>
          prev.filter((item) => getAffiliationId(item) !== affiliationId)
        );
        Swal.fire(
          "Deleted!",
          "The affiliation has been deleted successfully.",
          "success"
        );
        fetchData();
      } catch (error) {
        handleErrors(error);
      }
    }
  };

  const renderWebsiteCell = (url) => {
    if (!url) {
      return "-";
    }
    const href = normalizeWebsiteHref(url);
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="xc-cell-link"
      >
        {url}
      </a>
    );
  };

  const renderLogoCell = (logoUrl) => (
    <img
      src={logoUrl || allImages.DefultImage}
      alt="Affiliation Logo"
      className="rounded img-thumbnail"
      style={{ width: "48px", height: "48px", objectFit: "contain" }}
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = allImages.DefultImage;
      }}
    />
  );

  return (
    <>
      <ComponentHeader title="Affiliations Master" />

      {pageAccessDetails.addAccess ? (
        <AddAffiliation
          editMode={editMode}
          initialData={selectedAffiliation ?? {}}
          onSuccess={fetchData}
          setSelectedAffiliation={setSelectedAffiliation}
          setEditMode={setEditMode}
          isOpen={isFormOpen}
          onClose={() => setIsFormOpen(false)}
        />
      ) : null}

      {pageAccessDetails.viewAccess ? (
        <div className="row">
          <div className="col-xxl-12">
            <div className="card mt-xxl-n5 xc-list-card">
              <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div>
                  <h5 className="mb-0">Manage Affiliations</h5>
                  <span className="xc-list-count">
                    {filteredData.length}{" "}
                    {filteredData.length === 1
                      ? "affiliation"
                      : "affiliations"}
                  </span>
                </div>
                {pageAccessDetails.addAccess ? (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      setSelectedAffiliation(null);
                      setEditMode(false);
                      setIsFormOpen(true);
                    }}
                  >
                    <i className="ri-add-line me-1" aria-hidden="true" />
                    Add Affiliation
                  </button>
                ) : null}
              </div>
              <div className="card-body manage-amenity-master-card-body">
                <TableToolbar
                  entriesPerPage={entriesPerPage}
                  onEntriesChange={handleEntriesChange}
                  searchValue={searchQuery}
                  searchPlaceholder="Search affiliations..."
                  onSearchChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  onRefresh={fetchData}
                />

                {loading ? (
                  <Loading />
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle table-bordered">
                      <TableHeader
                        columns={[
                          "Sl No",
                          "Logo",
                          "Affiliation Name",
                          "Website URL",
                          "Display Order",
                          "Status",
                          "Action",
                        ]}
                      />
                      <tbody className="manage-page-group-table-values">
                        {currentData.length === 0 ? (
                          <TableDataStatusError colspan="7" />
                        ) : (
                          currentData.map((item, index) => {
                            const websiteUrl = getWebsiteUrl(item);
                            return (
                              <TablesRow
                                key={getAffiliationId(item)}
                                rowData={{
                                  slNo:
                                    (currentPage - 1) * entriesPerPage +
                                    index +
                                    1,
                                  logo: renderLogoCell(getLogoUrl(item)),
                                  affiliationName: getAffiliationName(item),
                                  websiteUrl: renderWebsiteCell(websiteUrl),
                                  displayOrder: getDisplayOrder(item),
                                  status: renderStatusBadge(item),
                                }}
                                columns={[
                                  "slNo",
                                  "logo",
                                  "affiliationName",
                                  "websiteUrl",
                                  "displayOrder",
                                  "status",
                                ]}
                                hideIcons={false}
                                onEdit={() => {
                                  setSelectedAffiliation(item);
                                  setEditMode(true);
                                  setIsFormOpen(true);
                                }}
                                onDelete={() => handleDelete(getAffiliationId(item))}
                                pageLevelAccessData={pageAccessDetails}
                              />
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalEntries={filteredData.length}
                  entriesPerPage={entriesPerPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        ""
      )}
    </>
  );
};
